import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { getPharmacyTypeBadgeStyles } from "../table/utils/getPharmacyTypeBadgeStyles";
import {
  getNycbsClientOutcomeBadgeStyles,
  getNycbsClientOutcomeDisplayText,
  NycbsClientOutcome,
} from "../table/utils/nycbsClientOutcome";
import {
  NycbsExternalLogsModalData,
  NycbsExternalLogsStageRow,
} from "../types/nycbsExternalLogsModalData";
import { SftpPushTrailRow } from "../types/sftpPushTrail";

const EMPTY_VALUE = "--";

/** Value the trail writer records on a push attempt that did not land. */
const PUSH_STATUS_ERROR = "error";

const INPUT_BATCH_STAGE = "Input Batch";
const FAILED_PUSH_STAGE = "Failed Push";

/** Batch numbering shown to the client; beyond this the arabic number is used. */
const BATCH_NUMERALS = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
];

/**
 * Resolves the CMM key shown in the Logs modal from available order fields.
 *
 * @input order - Raw NYCBS External order row
 * @return string: CoverMyMeds key or placeholder
 */
const resolveCmmKey = (order: PharmaStpFileModel): string => {
  const candidates = [
    order.covermymed_id,
    order.cmm_result_key,
    order.chat_key,
    order.doc_key,
  ];

  for (const value of candidates) {
    if (!value || value === "nan" || value === "NaN") {
      continue;
    }
    return String(value);
  }

  return EMPTY_VALUE;
};

const EMPTY_OUTCOME_FIELDS: Pick<
  NycbsExternalLogsStageRow,
  "outcomeText" | "outcomeColor" | "outcomeBgColor"
> = {
  outcomeText: EMPTY_VALUE,
  outcomeColor: "#000000",
  outcomeBgColor: "#F5F5F5",
};

const RISA_IN_PROGRESS_OUTCOME_FIELDS: Pick<
  NycbsExternalLogsStageRow,
  "outcomeText" | "outcomeColor" | "outcomeBgColor"
> = (() => {
  const outcomeText = getNycbsClientOutcomeDisplayText(null);
  const styles = getNycbsClientOutcomeBadgeStyles(outcomeText);

  return {
    outcomeText,
    outcomeColor: styles.color,
    outcomeBgColor: styles.bgColor,
  };
})();

/**
 * Badge for a push that never landed.
 *
 * Reuses the Denied palette rather than introducing a second red: both are
 * terminal-negative states, and the label carries the distinction.
 */
const FAILED_PUSH_OUTCOME_FIELDS: Pick<
  NycbsExternalLogsStageRow,
  "outcomeText" | "outcomeColor" | "outcomeBgColor"
> = (() => {
  const styles = getNycbsClientOutcomeBadgeStyles(NycbsClientOutcome.DENIED);

  return {
    outcomeText: "Delivery Failed",
    outcomeColor: styles.color,
    outcomeBgColor: styles.bgColor,
  };
})();

/**
 * Builds outcome badge fields for a logs stage row from response_status.
 *
 * @input responseStatus - Raw response_status recorded on the push
 * @return object: Outcome label and badge colors
 */
const buildOutcomeFields = (
  responseStatus: string | null | undefined,
): Pick<
  NycbsExternalLogsStageRow,
  "outcomeText" | "outcomeColor" | "outcomeBgColor"
> => {
  if (responseStatus == null || responseStatus === "") {
    return EMPTY_OUTCOME_FIELDS;
  }

  const outcomeText = getNycbsClientOutcomeDisplayText(responseStatus);
  const styles = getNycbsClientOutcomeBadgeStyles(outcomeText);

  return {
    outcomeText,
    outcomeColor: styles.color,
    outcomeBgColor: styles.bgColor,
  };
};

/**
 * Names a delivered batch by its position among successful pushes.
 *
 * @input deliveredCount - 1-based count of batches actually delivered so far
 * @return string: Stage label, e.g. "Output Batch II"
 */
const buildDeliveredStageLabel = (deliveredCount: number): string => {
  const numeral = BATCH_NUMERALS[deliveredCount - 1] ?? String(deliveredCount);
  return `Output Batch ${numeral}`;
};

/**
 * Turns recorded pushes into stage rows, oldest first.
 *
 * Failed pushes are shown, not hidden — the trail records them deliberately and
 * suppressing them in the only UI that reads it would defeat the audit. They do
 * not consume a batch number, so "Output Batch II" stays the second batch the
 * client actually received.
 *
 * @input trailRows - Recorded pushes for the case, in pushed_at order
 * @input fallbackCmmKey - Key to show when a push recorded none
 * @return NycbsExternalLogsStageRow[]: One stage row per recorded push
 */
const buildOutputStages = (
  trailRows: SftpPushTrailRow[],
  fallbackCmmKey: string,
): NycbsExternalLogsStageRow[] => {
  let deliveredCount = 0;

  return trailRows.map((row) => {
    const isFailure =
      (row.status ?? "").trim().toLowerCase() === PUSH_STATUS_ERROR;

    if (!isFailure) {
      deliveredCount += 1;
    }

    return {
      stage: isFailure
        ? FAILED_PUSH_STAGE
        : buildDeliveredStageLabel(deliveredCount),
      filename: row.output_filename?.trim() || EMPTY_VALUE,
      // The key as of this push, not the case's current key — a resubmit can
      // re-key a case, and showing today's key on an old batch hides that.
      cmmKey: row.external_key?.trim() || fallbackCmmKey,
      ...(isFailure
        ? FAILED_PUSH_OUTCOME_FIELDS
        : buildOutcomeFields(row.response_status)),
    };
  });
};

/**
 * Builds the Logs modal view model from an order row and its recorded pushes.
 *
 * @input order - Raw order from the NYCBS External API
 * @input trailRows - Recorded SFTP pushes for the case, oldest first
 * @return NycbsExternalLogsModalData: Populated modal data for the selected order
 */
export const buildNycbsExternalLogsModalData = (
  order: PharmaStpFileModel,
  trailRows: SftpPushTrailRow[] = [],
): NycbsExternalLogsModalData => {
  const cmmKey = resolveCmmKey(order);

  return {
    seq: String(order.seq ?? order.sequence ?? "").trim() || EMPTY_VALUE,
    drug: order.drug?.trim() || EMPTY_VALUE,
    mrn: order.patient_mrn?.trim() || EMPTY_VALUE,
    pharmacyType: getPharmacyTypeBadgeStyles(order.pharmacy_type),
    patientName: order.patient_name?.trim() || EMPTY_VALUE,
    cmmKey,
    stages: [
      {
        stage: INPUT_BATCH_STAGE,
        filename: order.filename?.trim() || EMPTY_VALUE,
        cmmKey,
        ...RISA_IN_PROGRESS_OUTCOME_FIELDS,
      },
      ...buildOutputStages(trailRows, cmmKey),
    ],
  };
};
